import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const buttonVariants = cva(
  [
    // Layout
    'inline-flex items-center justify-center',
    // Typography
    'text-center font-medium tracking-wide',
    // Interaction
    'cursor-pointer outline-hidden transition-all duration-200',
    // Focus ring
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-this-line-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
    // States
    'disabled:pointer-events-none disabled:select-none disabled:opacity-70',
  ],
  {
    variants: {
      variant: {
        filled: [
          'bg-this-solid text-white', // Default
          'hover:bg-this-solid-hover', // Hover
          'focus-visible:bg-this-solid-hover', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
        ],
        soft: [
          'bg-this-bg text-this-text', // Default
          'hover:bg-this-bg-hover', // Hover
          'focus-visible:bg-this-bg-hover', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
        ],
        outlined: [
          'border border-this-line text-this-text', // Default
          'hover:bg-this-bg', // Hover
          'focus-visible:bg-this-bg', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
        ],
        'soft-outlined': [
          'bg-this-bg border border-this-line text-this-text', // Default
          'hover:bg-this-bg-hover', // Hover
          'focus-visible:bg-this-bg-hover', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
        ],
        flat: [
          'text-this-text', // Default
          'hover:bg-this-bg', // Hover
          'focus-visible:bg-this-bg', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
        ],
      },
      size: {
        xs: 'px-3 py-1 text-xs',
        sm: 'px-4 py-1.5 text-sm',
        md: 'px-5 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
      },
      rounded: {
        default: 'rounded-lg',
        full: 'rounded-full',
      },
      glow: {
        true: 'shadow-lg shadow-this-solid/50',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'filled',
      size: 'md',
      rounded: 'default',
      glow: false,
    },
  }
);

type ButtonProps = {
  color?: ColorType;
  isIcon?: boolean;
} & VariantProps<typeof buttonVariants> &
  ComponentPropsWithoutRef<'button'>;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      color = 'neutral',
      glow,
      isIcon = false,
      type = 'button',
      children,
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        buttonVariants({ variant, size, rounded, glow }),
        isIcon && 'shrink-0 p-0',
        setThisClass(color),
        className
      )}
      {...rest}
    >
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export { Button };
export type { ButtonProps };
